import type { AlertNoteVariant } from './index.js';

/**
 * Which note the inventory panel is drawing for a row, and the facts that note
 * states — the single place the sweep, the alerts card and the Logistics KPI
 * agree on what a row is currently saying.
 *
 * The panel renders ONE line per row (a first-match-wins chain in
 * `InventoryRow`), so a row raises at most one alert. Reproducing that chain
 * here is what makes "every red or amber note is an alert" literally true
 * rather than approximately: if the row is silent, nothing fires; if it speaks,
 * exactly the sentence on screen is what the sweep repeats.
 *
 * Green and neutral notes return null. They are statements and questions, not
 * problems: a delivery arriving on time, a landed one waiting to be counted,
 * "did it arrive?" on a row that still has cover.
 *
 * It lives in `shared` rather than in the backend because the KPI tile counts
 * the same four groups the nightly sweep raises, off the same rows. A second
 * copy of this chain would not fail to compile — the tile would just quietly
 * disagree with the card about how many materials are in trouble, which is the
 * kind of break nobody notices.
 */
export interface RowNote {
  variant: AlertNoteVariant;
  /** The figure the sentence interpolates — a day gap or a day count. 0 where
   *  the sentence names no number. */
  value: number;
  /** The date the sentence names ('YYYY-MM-DD'), when it names one. */
  date?: string;
  /** The order the sentence names, when it names one. */
  ref?: string;
}

/**
 * The fields of an inventory level the note chain reads. Structural on purpose:
 * the backend's `InventoryLevel` and the frontend's `ILogistics` mirror of it
 * both satisfy this without either package importing the other's domain types.
 */
export interface NoteLevel {
  needsCheck: boolean;
  awaitingRenewal: boolean;
  arrivalUnconfirmed: boolean;
  lastArrivalAt: string | null;
  lastArrivalDueDate: string | null;
  lastArrivalDueExact: boolean;
  lastArrivalRef: string | null;
  deliveryIncoming: boolean;
  incomingEta: string | null;
  incomingEtaExact: boolean;
  incomingRef: string | null;
  overdue: boolean;
  leadTimeDays: number | null;
  daysLeft: number;
  draftOrderRef: string | null;
}

/** The fields of an untracked material the note chain reads. */
export interface NoteMaterial {
  deliveryRef: string;
  deliveredAt: string | null;
  dueDate: string | null;
}

/** One formatter per zone — building an `Intl.DateTimeFormat` is the expensive
 *  part, and a board sort or a sweep asks for the same zone thousands of times. */
const dayFormatters = new Map<string, Intl.DateTimeFormat>();

/** ISO instant → the calendar day (YYYY-MM-DD) it fell on in `tz`. `en-CA` is
 *  used purely because it renders as YYYY-MM-DD. */
export function dayInZone(date: Date, tz: string): string {
  let formatter = dayFormatters.get(tz);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA', { timeZone: tz });
    dayFormatters.set(tz, formatter);
  }
  return formatter.format(date);
}

/** Whole days `day` is past `from`, both bare 'YYYY-MM-DD'. Negative when it is
 *  still ahead. Pure day arithmetic — both inputs are already calendar days in
 *  the site's zone, so no timezone applies here. */
function daysAfter(day: string, from: string): number {
  return Math.round((Date.parse(day) - Date.parse(from)) / 86_400_000);
}

/**
 * The note a TRACKED row is showing, or null when it is quiet.
 *
 * `today` is the site's calendar day and `tz` its zone — a site is already on
 * tomorrow while UTC is still on today, and every branch below turns on which
 * day it is there. `lastArrivalAt` is an INSTANT, so it has to be converted
 * before it can be compared with a due date: slicing its ISO string takes the
 * UTC day, which on a UTC+9 site is the previous day for anything logged after
 * 15:00 UTC — the whole working morning. The panel converts it with `zonedDay`
 * (InventoryRow), and a note that disagreed with the row it quotes would defeat
 * the point of quoting it.
 */
export function trackedRowNote(level: NoteLevel, today: string, tz: string): RowNote | null {
  /** The site-local day a landed arrival fell on. */
  const arrivalDay = (iso: string): string => dayInZone(new Date(iso), tz);
  // A ran-out row draws only its provenance line; the chain below is skipped
  // entirely (`needsCheck ? null : …`), so it can never also be a gap.
  if (level.needsCheck) {
    if (level.awaitingRenewal && level.lastArrivalAt != null) {
      return { variant: 'ran-out-arrived', value: 0, date: arrivalDay(level.lastArrivalAt) };
    }
    if (level.arrivalUnconfirmed) return { variant: 'ran-out-due', value: 0 };
    // A replacement on its way and not yet due: the row says "New delivery
    // arriving …", not "No delivery was scheduled" — so the alert must too.
    if (level.deliveryIncoming && !level.awaitingRenewal) {
      return {
        variant: level.incomingEta != null && !level.incomingEtaExact ? 'ran-out-incoming-about' : 'ran-out-incoming',
        value: 0,
        ...(level.incomingEta != null ? { date: level.incomingEta } : {}),
        ...(level.incomingRef != null ? { ref: level.incomingRef } : {}),
      };
    }
    return { variant: 'ran-out', value: 0 };
  }

  // Renewal beats in-flight beats the reorder gap — the panel's own order.
  if (level.awaitingRenewal) {
    // Only against a date the supplier committed to. `lastArrivalDueExact`
    // false means the due date was our own projection, and a nightly alert
    // must not accuse a supplier of missing a date they never gave.
    if (level.lastArrivalAt != null && level.lastArrivalDueDate != null && level.lastArrivalDueExact) {
      const late = daysAfter(arrivalDay(level.lastArrivalAt), level.lastArrivalDueDate);
      if (late > 0) {
        return {
          variant: 'late-arrived',
          value: late,
          date: level.lastArrivalDueDate,
          ...(level.lastArrivalRef != null ? { ref: level.lastArrivalRef } : {}),
        };
      }
    }
    return null; // "New delivery arrived" — a neutral statement.
  }

  if (level.deliveryIncoming) {
    if (level.incomingEta != null && level.incomingEtaExact) {
      const late = daysAfter(today, level.incomingEta);
      if (late > 0) {
        return {
          variant: 'late-in-flight',
          value: late,
          date: level.incomingEta,
          ...(level.incomingRef != null ? { ref: level.incomingRef } : {}),
        };
      }
    }
    return null; // Arriving, or late only against a date we projected.
  }

  if (level.overdue && level.leadTimeDays != null) {
    if (level.draftOrderRef != null) {
      return { variant: 'gap-draft', value: 0, ref: level.draftOrderRef };
    }
    const gap = level.leadTimeDays - level.daysLeft;
    return gap > 0 ? { variant: 'gap', value: gap } : { variant: 'gap-today', value: 0 };
  }

  return null;
}

/**
 * The note an UNTRACKED row is showing, or null when it is quiet.
 *
 * Only the amber state alerts: the order's day has come and nobody has said the
 * material arrived, so there is stock on site that nothing is counting. An
 * order still in the future is nothing to act on, and one already marked
 * delivered draws the same green line a tracked row does.
 */
export function untrackedRowNote(material: NoteMaterial, today: string): RowNote | null {
  if (material.deliveredAt != null) return null;
  if (material.dueDate == null || material.dueDate > today) return null;
  return { variant: 'untracked-due', value: 0, date: material.dueDate, ref: material.deliveryRef };
}

/**
 * The four Deliveries & Stock groups. One per red/amber note the inventory
 * panel can draw, so every note the panel treats as a problem has somewhere to
 * land — in the sweep, in the alerts card's section, and in the Logistics KPI.
 */
export const LOGISTICS_ALERT_TYPES = [
  'stock-gap',
  'stock-ran-out',
  'delivery-late',
  'stock-untracked',
] as const;
export type LogisticsAlertType = (typeof LOGISTICS_ALERT_TYPES)[number];

/** Which group repeats which note. Derived from the variant so the detector,
 *  the card and the KPI can never disagree about where a note belongs. */
export const GROUP_BY_VARIANT: Record<AlertNoteVariant, LogisticsAlertType> = {
  gap: 'stock-gap',
  'gap-today': 'stock-gap',
  'gap-draft': 'stock-gap',
  'ran-out': 'stock-ran-out',
  'ran-out-arrived': 'stock-ran-out',
  'ran-out-due': 'stock-ran-out',
  'ran-out-incoming': 'stock-ran-out',
  'ran-out-incoming-about': 'stock-ran-out',
  'late-in-flight': 'delivery-late',
  'late-arrived': 'delivery-late',
  'untracked-due': 'stock-untracked',
};
